import React, { createContext, useContext, useState, useEffect } from 'react';
import { client } from "../services/api";
import { useCookies } from 'react-cookie';
import { toast } from 'react-hot-toast';
import { toastConfig, successToastConfig, dangerToastConfig } from '../utils/data';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [role, setRole] = useState(null);
    const [organization, setOrganization] = useState(null);
    const [authToken, setAuthToken] = useState(
        localStorage.getItem("token") || null
    );
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userOrgs, setUserOrgs] = useState([]);
    const [hasError, setHasError] = useState(false);
    const [error, setError] = useState(null);
    const [signupError, setSignupError] = useState(null);
    const [emailError, setEmailError] = useState(null);
    const [editPasswordError, setEditPasswordError] = useState(null);
    const [editPasswordSuccess, setEditPasswordSuccess] = useState(null);
    const [emailUpdated, setEmailUpdated] = useState(false);
    const [cookies, setCookie] = useCookies(['veritas']);
    const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);

    // Helper to update timezone if it changes
    const detectAndSetTimezone = () => {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        setTimezone(tz);
    };

    useEffect(() => {
        detectAndSetTimezone();
        const checkAuth = async () => {
            try {
                const token = localStorage.getItem("token");
                const org = localStorage.getItem("org");
                if (token) {
                    const res = await client.me();
                                        if (res.ok && res.data.user) {
                        setUser(res.data.user);
                        setIsAuthenticated(true);
                        setAuthToken(token);
                        detectAndSetTimezone();
                        // toast('You are logged in.', successToastConfig);
                    } else {
                        // Clear invalid token
                        localStorage.removeItem("token");
                        setCookie('token', null, { path: '/' });
                        setAuthToken(null);
                        setUser(null);
                        setIsAuthenticated(false);
                    }
                }
                setLoading(false);
            } catch (error) {
                console.error('Auth check failed:', error);
                // Clear invalid token
                localStorage.removeItem("token");
                setCookie('token', null, { path: '/' });
                setAuthToken(null);
                setUser(null);
                setIsAuthenticated(false);
                setLoading(false);
            }
        };
        checkAuth();
    }, []);

    const logIn = async (data) => {
        // setLoading(true);
        try {
            const org = localStorage.getItem("org");
                        let d = {
                email: data.email,
                password: data.password,
                org: org
            }
            const authResult = await client.logIn(d);
                        if (!authResult.ok) {
                throw authResult.data;
            }
                        let u = authResult.data.user;
            const token = authResult.data.token;
            setAuthToken(token);
            if(u.organization?._id) {
                localStorage.setItem("org", u.organization._id);
            }
            localStorage.setItem("token", token);
            setCookie('token', token, { path: '/' });
            setUser(u);
            setIsAuthenticated(true);
            setHasError(false);
            setError(null);
            detectAndSetTimezone();
            toast('You are logged in.', successToastConfig);
            return u;
        } catch (error) {
            toast('Error logging in.', dangerToastConfig);
            setHasError(true);
            if(error) {
                setError(error.message);
            }
            return false;
        } finally {
            setLoading(false);
        }
    };

    const logOut = () => {
        localStorage.removeItem("token");
        localStorage.removeItem("org");
        setCookie('token', null, { path: '/' });
        setAuthToken(null);
        setUser(null);
        setIsAuthenticated(false);
        toast('You are logged out.', toastConfig);
    };

    const signUp = async (data) => {
        setLoading(true);
        setSignupError(null);
        try {
            const payload = {
                email: data.email,
                firstname: data.firstname,
                lastname: data.lastname,
                password: data.password,
                accountId: data.accountId,
                ic: data.ic,
                id: data.id,
            };
            const res = await client.signUp(payload);
                        if(res.status == 200) {
                const token = res.data.token;
                setAuthToken(token);
                localStorage.setItem("token", token);
                setCookie('token', token, { path: '/' });
                let u = res.data.user;
                u['organization'] = res.data.org;
                if(u.organization?._id) {
                    localStorage.setItem("org", u.organization._id);
                }
                setUser(u);
                setIsAuthenticated(true);
                setHasError(false);
                setError(null);
                detectAndSetTimezone();
                toast('Signup successful.', successToastConfig);
                return true;
            }else{
                toast(res.data?.message || 'Signup failed.', dangerToastConfig);
                setHasError(true);
                return false;
            }
        } catch (err) {
            toast('Error signing up.', dangerToastConfig);
            setHasError(true);
            if(err) {
                setError(err.message);
            }
            return false;
        } finally {
            setLoading(false);
        }
    };
  
    const handleUser = (u) => {
        setUser(u);
    }

    const handleOrganization = (org) => {
        setOrganization(org);
    };

    const value = {
        user,
        role,
        organization,
        signUp,
        logIn,
        logOut,
        loading,
        submitting,
        isAuthenticated,
        userOrgs,
        hasError,
        error,
        signupError,
        emailError,
        emailUpdated,
        editPasswordError,
        editPasswordSuccess,
        timezone
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};
